import { useCallback, useState } from "react";

import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import TileContent from "ds/components/Tile/Content";
import TileWrapper from "ds/components/Tile/Wrapper";
import Typography from "ds/components/Typography";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import { Clock, TickCircleFilled } from "components/icons/generated";
import useUserGuideProgressMutations from "shared/UserGuides/useUserGuideProgressMutations";
import { formatCertificateIssuedDate } from "shared/UserGuides/utils";
import useAnalytics from "hooks/useAnalytics";
import useOpenLearnGuide from "components/AssistantPanel/Learn/useOpenLearnGuide";
import useAssistantPanelLearnActiveGuide from "components/AssistantPanel/Learn/useActiveGuide";
import { showUserGuideGroupSwitchConfirmationModal } from "components/UserGuideGroup/SwitchConfirmationModal";
import { showGenerateCertificateModal } from "views/Account/LaunchPad/UserGuidesGroup/SidePanel/GenerateCertificateModal";
import { showCertificateDetailsDrawer } from "views/Account/MissionControl/CertificateDetailsDrawer";
import useUserGuideDownloadCertificate from "shared/UserGuides/useUserGuideDownloadCertificate";
import {
  UNLOCK_CERTIFICATION_EVENT_NAME,
  USER_GUIDE_START_EVENT_NAME,
  UserGuideStartLocation,
} from "shared/UserGuides/analytics";
import badgeLockedSrc from "shared/UserGuides/assets/badge-locked.svg";
import badgeUnlockedSrc from "shared/UserGuides/assets/badge-unlocked.svg";
import Tooltip from "ds/components/Tooltip";

import { MissionControlGroup } from "../useData";
import styles from "./styles.module.css";

type MissionControlCertificateTileProps = {
  group: MissionControlGroup;
};

const MissionControlCertificateTile = ({ group }: MissionControlCertificateTileProps) => {
  const {
    id: groupId,
    name: groupName,
    certificate,
    allGuidesCompleted,
    hasAnyProgress,
    hasInProgressGuide,
    nextGuideId,
    guidesCount,
    timeToComplete,
  } = group;

  const openLearnGuide = useOpenLearnGuide();
  const { startUserGuide } = useUserGuideProgressMutations();
  const { activeGuide } = useAssistantPanelLearnActiveGuide();
  const [isStarting, setIsStarting] = useState(false);

  const trackSegmentEvent = useAnalytics();

  const isEarned = !!certificate;
  const isComingSoon = !isEarned && guidesCount === 0;
  const isReadyToUnlock = allGuidesCompleted && !certificate && !isComingSoon;

  const openLearnPanel = useCallback(() => {
    openLearnGuide(groupId);
  }, [openLearnGuide, groupId]);

  const executeStart = useCallback(async () => {
    if (!nextGuideId) return;
    setIsStarting(true);
    trackSegmentEvent(USER_GUIDE_START_EVENT_NAME, {
      location: UserGuideStartLocation.MissionControl,
    });
    try {
      await startUserGuide({ id: nextGuideId });
      openLearnPanel();
    } finally {
      setIsStarting(false);
    }
  }, [nextGuideId, startUserGuide, openLearnPanel, trackSegmentEvent]);

  const handleStartOrContinue = useCallback(() => {
    if (hasInProgressGuide) {
      openLearnPanel();
      return;
    }

    if (activeGuide) {
      showUserGuideGroupSwitchConfirmationModal({
        activeGuideTitle: activeGuide.metadata.title,
        onConfirm: executeStart,
      });
    } else {
      executeStart();
    }
  }, [hasInProgressGuide, activeGuide, executeStart, openLearnPanel]);

  const handleUnlock = () => {
    trackSegmentEvent(UNLOCK_CERTIFICATION_EVENT_NAME, { mission: groupId });
    showGenerateCertificateModal({ groupId });
  };

  const { download, isDownloading } = useUserGuideDownloadCertificate(certificate, {
    mission: groupId,
  });

  const badgeSrc = isEarned ? badgeUnlockedSrc : badgeLockedSrc;
  const statusIcon = isEarned || isReadyToUnlock ? TickCircleFilled : Clock;

  const getStatusText = () => {
    if (isEarned) {
      return `Issued on ${formatCertificateIssuedDate(certificate.issuedAt)}`;
    }
    if (isReadyToUnlock) return "Complete";
    return `Complete in ${timeToComplete}`;
  };

  const getStatusTooltipText = () => {
    if (isComingSoon || isReadyToUnlock || isEarned) return undefined;
    return `Estimated time to complete: ${timeToComplete}`;
  };

  const getActionButtonLabel = () => {
    if (hasAnyProgress) return "Continue mission";
    return "Start mission";
  };

  const statusTooltipText = getStatusTooltipText();

  return (
    <TileWrapper className={styles.tile}>
      {isEarned && (
        <Box className={styles.action}>
          <DropdownMenuEllipsis tooltip="Certificate actions">
            <DropdownMenuItem
              onAction={() => showCertificateDetailsDrawer({ certificateId: certificate.id })}
            >
              See details
            </DropdownMenuItem>
            <DropdownMenuItem onAction={download} isDisabled={isDownloading}>
              Download as PNG
            </DropdownMenuItem>
          </DropdownMenuEllipsis>
        </Box>
      )}

      {isReadyToUnlock && (
        <Box className={styles.action}>
          <Button variant="secondary" size="medium" onPress={handleUnlock}>
            Unlock
          </Button>
        </Box>
      )}

      {!isEarned && !isReadyToUnlock && isComingSoon && (
        <Box className={styles.action}>
          <Badge type="weak" variant="gray" text="Coming soon" />
        </Box>
      )}

      {!isEarned && !isReadyToUnlock && !isComingSoon && (
        <Box className={styles.action}>
          <Button
            variant="secondary"
            size="medium"
            onPress={handleStartOrContinue}
            loading={isStarting}
            disabled={isStarting || !nextGuideId}
          >
            {getActionButtonLabel()}
          </Button>
        </Box>
      )}

      <TileContent gap="xx-large">
        <img src={badgeSrc} alt="" className={styles.badge} aria-hidden />
        <Box direction="column" gap="x-large">
          <Box direction="column" gap="small">
            <Typography variant="p-t8" tag="span" color="secondary" className={styles.courseLabel}>
              COURSE CERTIFICATE
            </Typography>
            <Typography variant="p-t6" tag="span">
              {isEarned ? certificate.course : groupName}
            </Typography>
          </Box>
          {!isComingSoon && (
            <Box direction="row" align="center" gap="small">
              <Icon src={statusIcon} color="secondary" />
              <Tooltip
                active={!!statusTooltipText}
                on={(props) => (
                  <Typography {...props} variant="p-body3" tag="span" color="secondary">
                    {getStatusText()}
                  </Typography>
                )}
              >
                {statusTooltipText}
              </Tooltip>
            </Box>
          )}
        </Box>
      </TileContent>
    </TileWrapper>
  );
};

export default MissionControlCertificateTile;
